import React, { useEffect, useRef, useState } from 'react';
import './ScratchCard.css';
import useBank from './useBank';
import { doc, increment, updateDoc } from 'firebase/firestore';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import useScratchCanvas from '../utils/useScratchCanvas';

const totalRows = 5;
const symbolsPerRow = 3;
const winningSymbol = '👑';
const diamondSymbol = '💎';
const nonWinningSymbols = ['🍌', '🍒', '🍇', '🍉', '🍍', '🍓', '🍑', '🍋'];
const probability = 0.036;
const diamondProbability = 0.000075;

const ScratchCard5 = ({ refreshUser }) => {
  const canvasRefs = useRef([]);
  const [isScratching, setIsScratching] = useState(false);
  const [elements, setElements] = useState(Array(totalRows).fill([]));
  const [revealedSymbols, setRevealedSymbols] = useState(Array(totalRows).fill([]));
  const [gameEnded, setGameEnded] = useState(false);
  const [message, setMessage] = useState('');
  const { withdrawMoney } = useBank();
  const { user } = useFireAuth();
  const { initCanvasBackground, getEventCoordinates, drawScratch } = useScratchCanvas();

  const incrementGamesPlayed = async () => {
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
          gamesPlayed: increment(1),
        });
      } catch (error) {
        console.error('Error incrementing games played:', error);
      }
    }
  };

  useEffect(() => {
    const initializeCanvas = (canvas, rowIndex) => {
      initCanvasBackground(canvas);

      const initialElements = Array.from({ length: symbolsPerRow }, (_, i) => {
        const randomValue = Math.random();
        let symbol;
        if (randomValue < diamondProbability) {
          symbol = diamondSymbol;
        } else if (randomValue < probability) {
          symbol = winningSymbol;
        } else {
          symbol = nonWinningSymbols[Math.floor(Math.random() * nonWinningSymbols.length)];
        }

        return {
          id: i,
          x: i * (canvas.width / symbolsPerRow),
          y: 0,
          width: canvas.width / symbolsPerRow,
          height: canvas.height,
          scratched: false,
          symbol,
        };
      });

      setElements(prevElements => {
        const newElements = [...prevElements];
        newElements[rowIndex] = initialElements;
        return newElements;
      });
    };

    canvasRefs.current.forEach((canvas, index) => {
      initializeCanvas(canvas, index);
    });
  }, [initCanvasBackground]);

  const handleScratch = (e, rowIndex) => {
    if (gameEnded) return;

    const canvas = canvasRefs.current[rowIndex];
    const { x, y } = getEventCoordinates(e, canvas);
    drawScratch(canvas, x, y);

    setElements(prevElements => {
      const newElements = [...prevElements];
      const rowElements = newElements[rowIndex];
      const updatedElements = rowElements.map(element => {
        if (!element.scratched && x > element.x && x < element.x + element.width && y > element.y && y < element.y + element.height) {
          element.scratched = true;
          setRevealedSymbols(prevRevealed => {
            const newRevealed = [...prevRevealed];
            newRevealed[rowIndex] = [...newRevealed[rowIndex], element.symbol];
            return newRevealed;
          });
        }
        return element;
      });
      newElements[rowIndex] = updatedElements;
      return newElements;
    });

    const updatedRevealedSymbols = [...revealedSymbols];
    updatedRevealedSymbols[rowIndex] = elements[rowIndex].filter(element => element.scratched).map(element => element.symbol);
    setRevealedSymbols(updatedRevealedSymbols);

    if (updatedRevealedSymbols[rowIndex].length === symbolsPerRow) {
      const allRowsRevealed = updatedRevealedSymbols.every(row => row.length === symbolsPerRow);
      if (allRowsRevealed && !gameEnded) {
        setGameEnded(true);
        setTimeout(async () => {
          try {
            const winningCount = updatedRevealedSymbols.flat().filter(symbol => symbol === winningSymbol).length;
            const diamondCount = updatedRevealedSymbols.flat().filter(symbol => symbol === diamondSymbol).length;
            const winnings = (winningCount * 10) + (diamondCount * 1000);
            if (winningCount > 0 || diamondCount > 0) {
              let msg = `You Win! You have ${winningCount} ${winningSymbol}(s)`;
              if (diamondCount > 0) {
                msg += ` and ${diamondCount} ${diamondSymbol}(s)`;
              }
              msg += ` and win $${winnings}`;
              setMessage(msg);
              await withdrawMoney(winnings);
              await refreshUser();
            } else {
              setMessage('Not a winner this time 🫤');
              await refreshUser();
            }
            await incrementGamesPlayed();
          } catch (error) {
            console.error('Error processing scratch card result:', error);
            setMessage('Error processing result. Please try again.');
          }
        }, 500);
      }
    }
  };

  return (
    <div className="scratch-card">
      <div className="scratch-card-rules">
        <p>Scratch and reveal any 👑 symbol to win, find a 💎 for a jackpot!</p>
      </div>
      {Array.from({ length: totalRows }).map((_, rowIndex) => (
        <div key={rowIndex} className="scratch-card-row">
          <div className="scratch-card-symbols">
            {elements[rowIndex].map((element, i) => (
              <div key={i} className="scratch-card-symbol">
                {element.symbol}
              </div>
            ))}
          </div>
          <canvas
            ref={el => canvasRefs.current[rowIndex] = el}
            width={320}
            height={80}
            className={`scratch-card-canvas ${revealedSymbols[rowIndex].length === symbolsPerRow ? 'fade-out' : ''}`}
            onMouseDown={() => setIsScratching(true)}
            onMouseUp={() => setIsScratching(false)}
            onMouseMove={(e) => isScratching && handleScratch(e, rowIndex)}
            onTouchStart={() => setIsScratching(true)}
            onTouchEnd={() => setIsScratching(false)}
            onTouchMove={(e) => isScratching && handleScratch(e, rowIndex)}
          />
        </div>
      ))}
      <div className="scratch-card-info">
        <p>{message}</p>
      </div>
    </div>
  );
};

export default ScratchCard5;
